import { fetchUserProfileById } from '../services/userService';
import { validateInviteToken, claimInvite } from '../services/inviteService';
import React, { createContext, useContext, useEffect, useState, useCallback } from 'react';
import { supabase } from '../lib/supabase';

export const AuthContext = createContext({});

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

// Helper function to extract tokens from URL
const extractTokensFromUrl = () => {
  const url = window.location.href;
  const urlParams = new URLSearchParams(window.location.search);

  // Handle hash-based URLs (most common for Supabase)
  let hashParams = new URLSearchParams();
  if (window.location.hash) {
    // Remove the # at the beginning
    const hashString = window.location.hash.substring(1);
    
    // Supabase URLs typically have the format: #access_token=...&type=recovery&...
    // So we can directly parse the hash as query parameters
    hashParams = new URLSearchParams(hashString);
  }

  // Check both URL search params and hash params (Supabase typically uses hash)
  const accessToken = hashParams.get('access_token') || urlParams.get('access_token');
  const refreshToken = hashParams.get('refresh_token') || urlParams.get('refresh_token');
  const token = hashParams.get('token') || urlParams.get('token');
  const type = hashParams.get('type') || urlParams.get('type');
  const expiresAt = hashParams.get('expires_at') || urlParams.get('expires_at');
  
  // Check for invite token
  const inviteToken = urlParams.get('invite');

  return { accessToken, refreshToken, token, type, expiresAt, inviteToken };
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [userRole, setUserRole] = useState(null);
  const [userProfile, setUserProfile] = useState(null);
  const [authEvent, setAuthEvent] = useState(null);
  const [isPasswordRecovery, setIsPasswordRecovery] = useState(false);
  const [isProcessingTokens, setIsProcessingTokens] = useState(false);
  const [inviteData, setInviteData] = useState(null);
  const [isValidatingInvite, setIsValidatingInvite] = useState(false);

  // Function to validate invite token
  const validateInvite = useCallback(async (token) => {
    setIsValidatingInvite(true);
    try {
      // DEBUG: Log token extraction details
      console.log('🔍 DEBUGGING: AuthContext token validation:', {
        rawToken: token,
        tokenLength: token?.length,
        urlSearchParams: window.location.search,
        extractedAgain: new URLSearchParams(window.location.search).get('invite'),
        timestamp: new Date().toISOString()
      });

      const { data, error } = await validateInviteToken(token);
      if (error || !data.valid) {
        console.error('❌ Invite validation failed in AuthContext:', error || data.error);
        console.error('Failed token details:', { token, currentUrl: window.location.href });
        setInviteData(null);
        return { valid: false, error: error || data.error };
      }
      
      console.log('✅ Invite validation succeeded in AuthContext');
      setInviteData(data);
      return { valid: true, data };
    } catch (err) {
      console.error('❌ Error validating invite in AuthContext:', err);
      setInviteData(null);
      return { valid: false, error: err.message };
    } finally {
      setIsValidatingInvite(false);
    }
  }, []);

  // Function to clear invite data
  const clearInviteData = () => {
    setInviteData(null);
  };

  // Check for recovery tokens and invite tokens in URL
  const checkForTokens = useCallback(async () => {
    const { accessToken, refreshToken, type, expiresAt, inviteToken } = extractTokensFromUrl();
    
    // Check for invite token first
    if (inviteToken) {
      console.log('🔍 DEBUGGING: Found invite token in URL:', {
        inviteToken,
        tokenLength: inviteToken.length,
        currentUrl: window.location.href,
        searchParams: window.location.search,
        hasSpecialChars: {
          hasPlus: inviteToken.includes('+'),
          hasSlash: inviteToken.includes('/'),
          hasEquals: inviteToken.includes('='),
          hasSpace: inviteToken.includes(' ')
        }
      });
      await validateInvite(inviteToken);
    }

    // Check if we have recovery tokens
    if (accessToken && type === 'recovery') {
      console.log('🔄 Recovery tokens detected in URL');
      setIsProcessingTokens(true);
      setIsPasswordRecovery(true);
      
      try {
        // Check if token is expired
        if (expiresAt) {
          const expirationTime = parseInt(expiresAt) * 1000; // Convert to milliseconds
          const currentTime = Date.now();
          if (currentTime > expirationTime) {
            console.error('Recovery token has expired');
            setIsPasswordRecovery(false);
            setIsProcessingTokens(false);
            return;
          }
        }

        // If we have a refresh token, try to set the session
        if (refreshToken) {
          const { data: sessionData, error: sessionError } = await supabase.auth.setSession({
            access_token: accessToken,
            refresh_token: refreshToken
          });

          if (sessionError) {
            console.error('Error setting recovery session:', sessionError);
            setIsPasswordRecovery(false);
          } else {
            console.log('✅ Recovery session set successfully');
            // Redirect to reset-password if not already there
            if (window.location.pathname !== '/reset-password') {
              window.history.replaceState({}, '', '/reset-password');
            }
          }
        } else {
          // If no refresh token (implicit flow), we might just need to let Supabase handle it
          // but we still flag that we are in recovery mode
          console.log('ℹ️ No refresh token found, relying on Supabase auto-detection');
        }
      } catch (error) {
        console.error('Error processing recovery tokens:', error);
        setIsPasswordRecovery(false);
      } finally {
        setIsProcessingTokens(false);
      }
    }
  }, [validateInvite]);

  useEffect(() => {
    setLoading(true);
    
    // Check for recovery tokens and invite tokens first
    checkForTokens();

    const { data: authListener } = supabase.auth.onAuthStateChange(
      async (event, session) => {
        console.log('🔐 Auth event:', event);
        setAuthEvent(event);

        if (event === 'PASSWORD_RECOVERY') {
          setIsPasswordRecovery(true);
        }
       
        if (session) {
          // Note: Active status checking is handled only in the signIn function
          // This prevents duplicate checks and conflicting UI messages
          
          setUser(session.user);
          
          // Fetch complete user profile including role and personal data
          const fetchUserProfile = async () => {
            try {
              // First try RPC function for role
              const { data: roleData, error: roleError } = await supabase.rpc('get_current_user_role');
              
              // Fetch full profile data
              const timeoutPromise = new Promise((_, reject) =>
                setTimeout(() => reject(new Error('Profile fetch timeout')), 5000)
              );
              
              const { data: profileData, error: profileError } = await Promise.race([
                supabase.rpc('get_user_with_coach', { user_id_param: session.user.id }),
                timeoutPromise
              ]);
              
              if (!profileError && profileData) {
                console.log('👤 Fetched complete user profile with coach:', profileData);
                setUserProfile(profileData);
                setUserRole(profileData.role);
              } else if (!roleError && roleData) {
                // If we got role from RPC but profile fetch failed, use role only
                console.warn('Profile fetch failed, using role from RPC:', roleError);
                setUserRole(roleData);
                setUserProfile(null);
              } else {
                // Both failed, use fallback
                console.error('Both profile and role fetch failed:', { profileError, roleError });
                setUserRole('CLIENT');
                setUserProfile(null);
              }
            } catch (error) {
              console.error('Error fetching user profile:', error);
              setUserRole('CLIENT');
              setUserProfile(null);
            }
            
            setLoading(false);
          };
          
          fetchUserProfile();
        } else {
          // If there is no session, we are done loading
          setUser(null);
          setUserRole(null);
          setUserProfile(null);
          setLoading(false); // <--- CRITICAL
        }
      }
    );


    return () => {
      authListener.subscription.unsubscribe();
    };
  }, []);

  const signUp = async (email, password, additionalData = {}) => {
    try {
      // Get invite token before starting signup process
      const inviteToken = new URLSearchParams(window.location.search).get('invite');
      
      // Step 1: If we have an invite, validate it's still claimable before creating user
      if (inviteData && inviteToken) {
        console.log('Validating invite before user creation...');
        
        // Re-validate the invite to ensure it's still claimable
        const revalidationResult = await validateInvite(inviteToken);
        if (!revalidationResult.valid) {
          throw new Error(`Invite is no longer valid: ${revalidationResult.error}`);
        }
        
        // Ensure email matches invite
        if (revalidationResult.data.email !== email) {
          throw new Error('Email address does not match the invitation');
        }
      }
      
      // Prepare metadata for user creation
      const metadata = { ...additionalData };
      
      // If we have invite data, merge it
      if (inviteData && inviteData.pre_filled_data) {
        Object.assign(metadata, inviteData.pre_filled_data);
      }

      // Step 2: Create the user account
      const { data, error } = await supabase.auth.signUp({
        email,
        password,
        options: {
          data: metadata
        }
      });

      if (error) {
        throw error;
      }

      if (!data?.user) {
        throw new Error('User creation failed - no user data returned');
      }

      const createdUser = data.user;

      // Step 3: If we have invite data, claim the invite immediately
      if (inviteData && inviteToken) {
        console.log('Claiming invite for user:', createdUser.id);
        
        const claimResult = await claimInvite(inviteToken, createdUser.id, additionalData);
        
        if (claimResult.error) {
          // Invite claiming failed - this is a critical error
          console.error('Critical: Failed to claim invite after user creation:', claimResult.error);
          
          // Note: We cannot rollback user creation from client side
          // The user account exists but invite was not claimed
          // This should be logged for manual cleanup by admin
          console.error('MANUAL CLEANUP REQUIRED: User created but invite not claimed', {
            userId: createdUser.id,
            email: createdUser.email,
            inviteToken: inviteToken,
            error: claimResult.error
          });
          
          throw new Error(`Registration completed but invitation could not be processed. Please contact support. Error: ${claimResult.error}`);
        }
        
        console.log('Invite claimed successfully');
        
        // Clear invite data after successful claim
        setInviteData(null);
        
        // Clean up URL
        const url = new URL(window.location);
        url.searchParams.delete('invite');
        window.history.replaceState({}, '', url);
      }

      // Both user creation and invite claiming (if applicable) succeeded
      return { data, error: null };
      
    } catch (err) {
      console.error('SignUp error:', err);
      return { data: null, error: err };
    }
  };

  const signIn = async (email, password) => {
    const { data, error } = await supabase.auth.signInWithPassword({
      email,
      password,
    });
    
    let userProfile = null;

    // If sign in was successful, check if user is active
    if (data?.user && !error) {
      try {
        const { data: profile, error: profileError } = await supabase
          .rpc('get_user_with_coach', { user_id_param: data.user.id });
          
        if (profileError) {
          console.error('Error checking profile during sign in:', profileError.message);
          // If we can't check, allow login but log the error
        } else if (profile && profile.is_active === false) {
          // Sign out inactive user immediately
          await supabase.auth.signOut({ scope: 'local' });
          return {
            data: null,
            error: {
              message: 'Your account has been deactivated. Please contact your administrator.'
            }
          };
        } else if (profile) {
          // Store profile data for immediate use (already includes coach data from RPC)
          console.log('👤 Storing user profile from sign-in:', profile);
          setUserProfile(profile);
          userProfile = profile;
        }
      } catch (activeError) {
        console.error('Error checking user active status during sign in:', activeError);
        // If we can't check active status, allow login but log the error
      }
    }
    
    return { data, error, profile: userProfile };
  };

  const signOut = async () => {
    try {
      // Use local scope instead of global to avoid 403 errors
      const { error } = await supabase.auth.signOut({ scope: 'local' });
      
      // Clear local state immediately regardless of error
      setUser(null);
      setUserRole(null);
      setUserProfile(null);
      setAuthEvent(null);
      setIsPasswordRecovery(false);
      
      if (error) {
        console.warn('Sign out warning:', error.message);
        // Even if there's an error, we've cleared local state
        // This handles cases where the session has already expired
      }
      
      return { error: null }; // Always return success since we've cleared local state
    } catch (err) {
      console.error('Sign out error:', err);
      // Clear local state even on error
      setUser(null);
      setUserRole(null);
      setUserProfile(null);
      setAuthEvent(null);
      setIsPasswordRecovery(false);
      return { error: null };
    }
  };

  const resetPassword = async (password) => {
    try {
      const { error } = await supabase.auth.updateUser({ password });
      if (!error) {
        setIsPasswordRecovery(false);
      }
      return { error };
    } catch (err) {
      return { error: err };
    } finally {
      // Clear URL parameters and hash after reset attempt
      window.history.replaceState({}, document.title, window.location.pathname);
    }
  };

  const deleteAccount = async () => {
    try {
      if (!user) {
        throw new Error('No user logged in');
      }

      // Step 1: Disable the user account in Supabase
      const { error: disableError } = await supabase.rpc('disable_user_account', {
        user_id_param: user.id
      });

      if (disableError) {
        console.error('Error disabling user account:', disableError);
        throw new Error('Failed to disable account: ' + disableError.message);
      }

      // Step 2: Trigger webhook for data deletion (if webhook URL is configured)
      const webhookUrl = import.meta.env.VITE_DELETE_ACCOUNT_WEBHOOK;
      if (webhookUrl) {
        try {
          const response = await fetch(webhookUrl, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({
              user_id: user.id,
              email: user.email,
              timestamp: new Date().toISOString(),
            }),
          });

          if (!response.ok) {
            console.error('Webhook notification failed:', response.statusText);
            // Continue with logout even if webhook fails
          }
        } catch (webhookError) {
          console.error('Error calling delete webhook:', webhookError);
          // Continue with logout even if webhook fails
        }
      }

      // Step 3: Sign out the user
      await signOut();

      return { error: null };
    } catch (err) {
      console.error('Delete account error:', err);
      return { error: err };
    }
  };

  const value = {
    user,
    loading,
    userRole,
    userProfile,
    authEvent,
    signUp,
    signIn,
    signOut,
    resetPassword,
    deleteAccount,
    isPasswordRecovery,
    isProcessingTokens,
    inviteData,
    isValidatingInvite,
    validateInvite,
    clearInviteData,
    checkForTokens,
    supabase,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};